import React, { useState } from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Spinner, Empty, StatusBadge } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";
import { Copy, Plus, ShieldX, Trash2, KeyRound, ServerCog } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

function PlatformTokensCard() {
    const { data: tokens, mutate, isLoading } = useSWR(
        "/api/admin/platform-tokens",
        fetcher,
    );
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: "", expires_in_days: "" });
    const [createdToken, setCreatedToken] = useState(null);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        try {
            const { data } = await api.post("/api/admin/platform-tokens", {
                name: form.name,
                expires_in_days: form.expires_in_days ? Number(form.expires_in_days) : null,
            });
            setCreatedToken(data);
            setForm({ name: "", expires_in_days: "" });
            setShowForm(false);
            await mutate();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const revoke = async (id) => {
        await api.post(`/api/admin/platform-tokens/${id}/revoke`);
        mutate();
    };
    const del = async (id) => {
        await api.delete(`/api/admin/platform-tokens/${id}`);
        mutate();
    };

    return (
        <div className="cm-card p-5" data-testid="platform-tokens-card">
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                    <ServerCog size={16} className="text-[#CCFF00]" />
                    <div className="cm-h3">Platform / Superadmin Tokens</div>
                </div>
                <button
                    data-testid="platform-token-new-button"
                    className="cm-btn-primary text-xs flex items-center gap-1.5"
                    onClick={() => setShowForm((v) => !v)}
                >
                    <Plus size={12} /> New token
                </button>
            </div>
            <p className="text-xs text-zinc-500 mb-4 max-w-2xl">
                Tokens with full <span className="font-mono text-zinc-300">admin:all</span> access
                for server-to-server integrations (e.g. reseller and cloud creation from the
                customer portal). Send in the header as
                {" "}<span className="font-mono text-zinc-300">Authorization: Bearer cm_admin_…</span>{" "}
                oder <span className="font-mono text-zinc-300">X-API-Key</span> senden.
            </p>

            {showForm && (
                <form
                    onSubmit={submit}
                    className="bg-cm-bg border border-cm-line p-4 mb-4 space-y-3 cm-fade-in"
                    data-testid="platform-token-form"
                >
                    <div>
                        <label className="cm-label">Name</label>
                        <input
                            data-testid="platform-token-form-name"
                            className="cm-input mt-1"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="e.g. customer portal integration"
                            required
                        />
                    </div>
                    <div>
                        <label className="cm-label">Expires in (days)</label>
                        <input
                            data-testid="platform-token-form-expires"
                            type="number"
                            className="cm-input mt-1 max-w-[200px]"
                            value={form.expires_in_days}
                            onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })}
                            placeholder="(empty = never)"
                        />
                    </div>
                    {err && <div className="text-xs text-cm-danger" data-testid="platform-token-err">{err}</div>}
                    <button
                        type="submit"
                        className="cm-btn-primary"
                        data-testid="platform-token-form-submit"
                        disabled={busy}
                    >
                        {busy ? "Creating…" : "Create token"}
                    </button>
                </form>
            )}

            {createdToken && (
                <div
                    className="bg-cm-brand/5 border border-cm-brand/40 p-4 mb-4 cm-fade-in"
                    data-testid="platform-token-created-banner"
                >
                    <div className="cm-label text-cm-brand mb-2">
                        Token created — copy now, it will not be shown again
                    </div>
                    <div className="font-mono text-xs break-all text-zinc-100 bg-cm-bg border border-cm-line p-3 flex items-start justify-between gap-3">
                        <span data-testid="platform-token-created-value">{createdToken.plaintext_token}</span>
                        <button
                            className="cm-btn-ghost text-xs flex items-center gap-1 shrink-0"
                            onClick={() => navigator.clipboard.writeText(createdToken.plaintext_token)}
                            data-testid="platform-token-copy-button"
                        >
                            <Copy size={12} /> Copy
                        </button>
                    </div>
                    <button
                        className="cm-btn-ghost text-xs mt-2"
                        onClick={() => setCreatedToken(null)}
                        data-testid="platform-token-dismiss"
                    >
                        Close
                    </button>
                </div>
            )}

            {isLoading ? (
                <Spinner />
            ) : !tokens?.length ? (
                <Empty
                    title="No platform tokens yet"
                    hint="Create a token to connect external systems via the API."
                />
            ) : (
                <div className="cm-table-wrap">
                    <table className="cm-table">
                        <thead>
                            <tr>
                                <th className="cm-th">Name</th>
                                <th className="cm-th">Prefix</th>
                                <th className="cm-th">Status</th>
                                <th className="cm-th">Expires</th>
                                <th className="cm-th">Last used</th>
                                <th className="cm-th"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tokens.map((t) => (
                                <tr key={t.id} data-testid={`platform-token-row-${t.id}`}>
                                    <td className="cm-td">{t.name}</td>
                                    <td className="cm-td font-mono text-xs text-zinc-400">{t.token_prefix}</td>
                                    <td className="cm-td">
                                        <StatusBadge status={t.revoked ? "suspended" : "active"} />
                                    </td>
                                    <td className="cm-td text-xs text-zinc-500">
                                        {t.expires_at ? fmtRel(t.expires_at) : "never"}
                                    </td>
                                    <td className="cm-td text-xs text-zinc-500">
                                        {t.last_used_at ? fmtRel(t.last_used_at) : "never"}
                                    </td>
                                    <td className="cm-td text-right">
                                        {!t.revoked && (
                                            <button
                                                className="cm-btn-ghost text-xs flex items-center gap-1"
                                                onClick={() => revoke(t.id)}
                                                data-testid={`platform-token-revoke-${t.id}`}
                                            >
                                                <ShieldX size={12} /> Revoke
                                            </button>
                                        )}
                                        <button
                                            className="cm-btn-ghost text-xs flex items-center gap-1 text-cm-danger"
                                            onClick={() => del(t.id)}
                                            data-testid={`platform-token-delete-${t.id}`}
                                        >
                                            <Trash2 size={12} /> Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default function TokensIndexPage() {
    const { data: resellers, isLoading } = useSWR("/api/admin/resellers", fetcher);
    return (
        <div data-testid="tokens-index-page">
            <PageHeader
                testid="tokens-index-header"
                title="API tokens"
                subtitle="Platform tokens for server-to-server integration and reseller-scoped tokens."
            />
            <div className="p-4 sm:p-6 space-y-6">
                <PlatformTokensCard />

                <div>
                    <div className="flex items-center gap-2 mb-3">
                        <KeyRound size={16} className="text-zinc-400" />
                        <div className="cm-h3">Reseller-Tokens</div>
                    </div>
                    <p className="text-xs text-zinc-500 mb-3">
                        Tokens are issued per reseller. Open a reseller to create, revoke or rotate tokens.
                    </p>
                    {isLoading ? (
                        <Spinner />
                    ) : !resellers?.length ? (
                        <Empty title="Create a reseller first" />
                    ) : (
                        <div className="cm-card overflow-hidden">
                            <div className="cm-table-wrap">
                                <table className="cm-table">
                                    <thead>
                                        <tr>
                                            <th className="cm-th">Reseller</th>
                                            <th className="cm-th">Slug</th>
                                            <th className="cm-th">Status</th>
                                            <th className="cm-th">Tokens</th>
                                            <th className="cm-th">Clouds</th>
                                            <th className="cm-th">Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {resellers.map((r) => (
                                            <tr key={r.id} className="cm-tr" data-testid={`tokens-reseller-${r.id}`}>
                                                <td className="cm-td">
                                                    <Link to={`/resellers/${r.id}`} className="text-cm-text hover:text-cm-brand">
                                                        {r.name}
                                                    </Link>
                                                </td>
                                                <td className="cm-td font-mono text-xs text-zinc-400">{r.slug}</td>
                                                <td className="cm-td"><StatusBadge status={r.status} /></td>
                                                <td className="cm-td font-mono">{r.tokens_count}</td>
                                                <td className="cm-td font-mono">{r.clouds_count}</td>
                                                <td className="cm-td text-xs text-zinc-500">{fmtRel(r.created_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
